import { useCallback, useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist/legacy/build/pdf";
import { Button } from "@/components/ui/button";
import { Loader2, ZoomIn, ZoomOut, FileWarning, Printer, Download } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

/** Renders a PDF page-by-page onto canvases, so it reads like a document
 *  instead of relying on the browser's plugin (which often just downloads). */
export default function PdfCanvasViewer({ src, data, filename = "document.pdf",
                                          className = "h-[520px]", testId = "pdf-canvas-viewer" }) {
  const holder = useRef(null);
  const [pages, setPages] = useState(0);
  const [scale, setScale] = useState(1.1);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const docRef = useRef(null);
  const renderToken = useRef(null);
  const bytes = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // pdf.js transfers (detaches) the buffer to its worker — hand it a copy so remounts still work.
    const task = pdfjs.getDocument(data ? { data: data.slice() } : src);
    task.promise.then((doc) => {
      if (cancelled) { doc.destroy(); return; }
      docRef.current = doc;
      setPages(doc.numPages);
    }).catch((err) => {
      if (!cancelled) setError(err?.message || "Could not read this PDF");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      task.destroy?.();
      docRef.current = null;
    };
  }, [src, data]);

  const blobUrl = useCallback(async () => {
    if (!bytes.current) {
      bytes.current = data ? data.slice()
        : new Uint8Array(await (await fetch(src)).arrayBuffer());
    }
    return URL.createObjectURL(new Blob([bytes.current], { type: "application/pdf" }));
  }, [src, data]);

  const download = async () => {
    const url = await blobUrl();
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const print = async () => {
    const url = await blobUrl();
    const frame = document.createElement("iframe");
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
    frame.src = url;
    frame.onload = () => {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    };
    document.body.appendChild(frame);
    setTimeout(() => { frame.remove(); URL.revokeObjectURL(url); }, 60000);
  };

  const draw = useCallback(async () => {
    const doc = docRef.current;
    const node = holder.current;
    if (!doc || !node) return;
    const token = Symbol("render");
    renderToken.current = token;
    node.innerHTML = "";
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    for (let n = 1; n <= doc.numPages; n += 1) {
      const page = await doc.getPage(n);
      if (renderToken.current !== token) return;
      const viewport = page.getViewport({ scale: scale * dpr });
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${viewport.width / dpr}px`;
      canvas.style.maxWidth = "100%";
      canvas.className = "mx-auto mb-4 rounded-md shadow-sm bg-white";
      canvas.setAttribute("data-testid", `${testId}-page-${n}`);
      node.appendChild(canvas);
      await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
      if (renderToken.current !== token) return;
    }
  }, [scale, testId]);

  useEffect(() => { if (pages) draw(); }, [pages, draw]);

  if (error) {
    return (
      <div className="p-10 text-center text-sm text-muted-foreground" data-testid={`${testId}-error`}>
        <FileWarning className="w-7 h-7 mx-auto mb-3 opacity-50" />
        {error}
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-background/60">
        <span className="text-xs text-muted-foreground" data-testid={`${testId}-pages`}>
          {pages ? `${pages} page${pages > 1 ? "s" : ""}` : "Opening…"}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`${testId}-zoom-out`}
                  onClick={() => setScale((s) => Math.max(0.6, +(s - 0.2).toFixed(2)))}>
            <ZoomOut className="h-3.5 w-3.5" />
          </Button>
          <span className="text-xs tabular-nums w-10 text-center">{Math.round(scale * 100)}%</span>
          <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`${testId}-zoom-in`}
                  onClick={() => setScale((s) => Math.min(2.4, +(s + 0.2).toFixed(2)))}>
            <ZoomIn className="h-3.5 w-3.5" />
          </Button>
          <span className="mx-1 h-4 w-px bg-border" />
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={print}
                  data-testid={`${testId}-print`}>
            <Printer className="h-3.5 w-3.5 sm:mr-1.5" />
            <span className="hidden sm:inline text-xs">Print</span>
          </Button>
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={download}
                  data-testid={`${testId}-download`}>
            <Download className="h-3.5 w-3.5 sm:mr-1.5" />
            <span className="hidden sm:inline text-xs">Download</span>
          </Button>
        </div>
      </div>
      <div className={`overflow-auto bg-muted/40 p-4 ${className}`}>
        {loading && (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Opening…
          </div>
        )}
        <div ref={holder} />
      </div>
    </div>
  );
}
